import apiClient from '../client'

/**
 * Metric suggestions (docs/specs/2026-10-05-ai-metric-suggestions-design.md §3.7).
 * Flat JSON under `/integrations/`. A run answers 200 with the result when the
 * rules answer alone, or 202 `{job_id}` while the model runs; poll
 * `fetchSuggestionRun`. Accepting answers 202 while Graphit sets the metric up;
 * poll `fetchSuggestion` until it is `applied` or `apply_failed`.
 */

const FLAT = { params: { include: null } }
/**
 * Starting a run can answer inline after up to about a minute (the rules,
 * then the model within its own budget): longer than the client's 30 s
 * default, so this one request waits up to 90 s.
 */
export const RUN_START_TIMEOUT_MS = 90_000
const runsPath = (connectionId) => `/integrations/connections/${connectionId}/suggestions/runs/`

/** @returns {Promise<{enabled: boolean, surfaces: Array<string>}>} */
export async function fetchSuggestionSettings() {
  const response = await apiClient.get('/integrations/suggestions/settings/', FLAT)
  const data = response?.data || {}
  const isOn = data.enabled === true && Array.isArray(data.surfaces)
  return { enabled: isOn, surfaces: isOn ? data.surfaces : [] }
}

/**
 * @param {string} connectionId
 * @param {{surface: string, topic?: string, k?: number}} request
 * @returns {Promise<{result: Object|null, jobId: string|null}>}
 */
export async function startSuggestionRun(connectionId, { surface, topic, k } = {}) {
  const body = { surface, ...(topic ? { topic } : {}), ...(k ? { k } : {}) }
  const response = await apiClient.post(runsPath(connectionId), body, { ...FLAT, timeout: RUN_START_TIMEOUT_MS })
  if (response.status === 202) return { result: null, jobId: response.data?.job_id ?? null }
  return { result: response.data ?? null, jobId: null }
}

/**
 * One poll of a run job. It waits as long as starting a run (K9): a slow
 * answer while the model runs must not read as a failed run.
 *
 * @param {string} connectionId
 * @param {string} jobId
 */
export async function fetchSuggestionRun(connectionId, jobId) {
  const response = await apiClient.get(`${runsPath(connectionId)}${jobId}/`, { ...FLAT, timeout: RUN_START_TIMEOUT_MS })
  return response.data
}

/**
 * @param {{status?: string, connectionId?: string, limit?: number, offset?: number}} [filters]
 * @returns {Promise<{results: Array<Object>, count: number}>}
 */
export async function fetchSuggestions({ status = 'pending', connectionId, limit = 25, offset = 0 } = {}) {
  const params = { include: null, status, limit, offset, ...(connectionId ? { connection: connectionId } : {}) }
  const response = await apiClient.get('/integrations/suggestions/', { params })
  return { results: response.data?.results ?? [], count: response.data?.count ?? 0 }
}

export async function fetchSuggestion(id) {
  const response = await apiClient.get(`/integrations/suggestions/${id}/`, FLAT)
  return response.data
}

export async function acceptSuggestion(id) {
  const response = await apiClient.post(`/integrations/suggestions/${id}/accept/`, {}, FLAT)
  return response.data?.suggestion
}

export async function dismissSuggestion(id, { reason = 'not_relevant', note } = {}) {
  const response = await apiClient.post(`/integrations/suggestions/${id}/dismiss/`, { reason, ...(note ? { note } : {}) }, FLAT)
  return response.data
}

export async function snoozeSuggestion(id, { days = 30 } = {}) {
  const response = await apiClient.post(`/integrations/suggestions/${id}/snooze/`, { days }, FLAT)
  return response.data
}

/** @returns {Promise<Array<string>>} */
export async function fetchSuggestionFocus() {
  const response = await apiClient.get('/integrations/suggestions/focus/', FLAT)
  return response.data?.focus_areas ?? []
}

/** @param {Array<string>} focusAreas */
export async function saveSuggestionFocus(focusAreas) {
  const response = await apiClient.put('/integrations/suggestions/focus/', { focus_areas: focusAreas }, FLAT)
  return response.data?.focus_areas ?? []
}
